import { Button, message } from "antd";
import type { CustomerReceivingAddress } from "@zhihaoscm/entity";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { addressService } from "@/api/addressService";
import { Breadcrumb, ProTable, ZhMessageBox, type ProColumn } from "@/components/common";
import { useFullAreaName } from "@/runtime/areaData";

/** 地址列复用旧版 areaLib 的行政区代码转名称规则。 */
function AddressText({ row }: { row: CustomerReceivingAddress }) {
    const areaName = useFullAreaName(row.province, row.city, row.district);
    return (
        <>
            {areaName}
            {row.detailAddress}
        </>
    );
}

/** 我的地址列表提供旧版完整的查询和维护操作。 */
export function AddressListPage() {
    const navigate = useNavigate();
    const [revision, setRevision] = useState(0);
    const [deleteId, setDeleteId] = useState<number>();
    /** 删除确认后刷新当前列表。 */
    const remove = async () => {
        if (deleteId == null) return;
        await addressService.deleteAddressById(deleteId);
        setDeleteId(undefined);
        setRevision(value => value + 1);
    };
    /** 默认地址接口要求提交原地址完整字段。 */
    const setDefault = async (row: CustomerReceivingAddress) => {
        const form = {
            contacts: row.contacts,
            cellphone: row.cellphone,
            detailAddress: row.detailAddress,
            areaCode: row.areaCode,
            landline: row.landline,
            subLandline: row.subLandline,
            zipCode: row.zipCode,
            isDefault: 1,
            province: row.province,
            city: row.city,
            district: row.district
        };
        await addressService.setDefault(row.id, form);
        setRevision(value => value + 1);
        void message.success("设置成功");
    };
    const columns = useMemo<ProColumn<CustomerReceivingAddress>[]>(
        () => [
            {
                key: "contacts",
                title: "联系人",
                minWidth: 142,
                search: { key: "keyword", type: "input", placeholder: "请输入联系人/联系电话", width: 120, span: 5 }
            },
            { key: "cellphone", title: "联系电话", minWidth: 142 },
            { key: "address", title: "地址", minWidth: 142, render: (_, row) => <AddressText row={row} /> },
            {
                key: "operation",
                title: "操作",
                width: 170,
                fixed: "right",
                render: (_, row) => (
                    <div className="address-list-actions">
                        <Button type="link" onClick={() => navigate(`/me/address/add?id=${row.id}`)}>
                            修改
                        </Button>
                        <Button type="link" onClick={() => setDeleteId(row.id)}>
                            删除
                        </Button>
                        {row.isDefault ? (
                            <span className="address-default">默认地址</span>
                        ) : (
                            <Button type="link" onClick={() => void setDefault(row)}>
                                设为默认
                            </Button>
                        )}
                    </div>
                )
            }
        ],
        [navigate]
    );
    const request = useMemo(
        () => async (params: Record<string, unknown>) => addressService.getAddressList(params),
        [revision]
    );
    return (
        <section className="address-list-page section-card">
            <Breadcrumb routes={[{ name: "账号管理" }, { name: "我的地址" }]} />
            <div className="address-list-table">
                <ProTable
                    key={revision}
                    title="我的地址"
                    rowKey="id"
                    columns={columns}
                    request={request}
                    header={() => (
                        <Button type="primary" onClick={() => navigate("/me/address/add")}>
                            新增
                        </Button>
                    )}
                />
            </div>
            <ZhMessageBox
                open={deleteId != null}
                tip="是否删除"
                onCancel={() => setDeleteId(undefined)}
                onSubmit={remove}
            />
        </section>
    );
}
